import ArrowBackOutlined from "@mui/icons-material/ArrowBackOutlined";
import AssignmentOutlined from "@mui/icons-material/AssignmentOutlined";
import AttachFileOutlined from "@mui/icons-material/AttachFileOutlined";
import BusinessOutlined from "@mui/icons-material/BusinessOutlined";
import ChildCareOutlined from "@mui/icons-material/ChildCareOutlined";
import CloseOutlined from "@mui/icons-material/CloseOutlined";
import FamilyRestroomOutlined from "@mui/icons-material/FamilyRestroomOutlined";
import GroupsOutlined from "@mui/icons-material/GroupsOutlined";
import HistoryOutlined from "@mui/icons-material/HistoryOutlined";
import VisibilityOutlined from "@mui/icons-material/VisibilityOutlined";
import { FlexGrid, FlexGridCellTemplate, FlexGridColumn } from "@grapecity/wijmo.react.grid";
import { FlexGridFilter } from "@grapecity/wijmo.react.grid.filter";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { enqueueSnackbar } from "notistack";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import CustomSimpleAccordion from "qubCommon/Accordions/CustomSimpleAccordion";
import {
  getMhdChildAttachmentBlob,
  getMhdChild,
} from "src/apis/controllers/mhdProfile";
import { formatDate, ProfileBreadcrumbs } from "./ProfileComponents";
import MhdFilePreviewDialog from "./MhdFilePreviewDialog";

const dateTimeFormatter = new Intl.DateTimeFormat("el-GR", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
});

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : dateTimeFormatter.format(date);
};

const CompactGrid = ({ items, children, rowHeight = 34, maxHeight = 440 }) => {
  const heightFor = (item) => typeof rowHeight === "function" ? rowHeight(item) : rowHeight;

  return (
    <FlexGrid
      className="mhd-profile-grid"
      itemsSource={items}
      isReadOnly
      autoClipboard
      selectionMode="CellRange"
      headersVisibility="All"
      alternatingRowStep={0}
      style={{ height: Math.min(maxHeight, 52 + items.reduce((height, item) => height + heightFor(item), 0)) }}
      initialized={(grid) => {
        grid.rows.defaultSize = 34;
        grid.columnHeaders.rows.defaultSize = 34;
        grid.rowHeaders.columns.defaultSize = 28;
      }}
      loadedRows={(grid) => {
        grid.rows.forEach((row) => {
          row.height = heightFor(row.dataItem);
        });
      }}
    >
      <FlexGridFilter />
      {children}
    </FlexGrid>
  );
};

const FileActions = ({ files, onPreview, openingFileId }) => files?.length ? (
  <Stack spacing={0} alignItems="flex-start">
    {files.map((file) => (
      <Button
        key={file.id}
        size="small"
        title={file.name}
        startIcon={<VisibilityOutlined />}
        disabled={openingFileId === file.id}
        onClick={() => onPreview(file)}
        sx={{ textTransform: "none", justifyContent: "flex-start" }}
      >
        {file.name}
      </Button>
    ))}
  </Stack>
) : "-";

const HistorySortHeader = ({ label, column, sortConfig, onSort }) => {
  const active = sortConfig.key === column;
  const direction = active ? sortConfig.direction : null;

  return (
    <th scope="col" aria-sort={direction || "none"}>
      <button type="button" className="mhd-profile-history-sort" onClick={() => onSort(column)}>
        {label}
        <i className={`fas fa-sort${direction === "ascending" ? "-up" : direction === "descending" ? "-down" : ""}`} aria-hidden="true" />
      </button>
    </th>
  );
};

const Field = ({ label, value }) => (
  <Box>
    <Typography variant="caption" >
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={600} sx={{ whiteSpace: "pre-wrap" }}>
      {value || "-"}
    </Typography>
  </Box>
);

const FieldGrid = ({ children }) => (
  <Box
    sx={{
      display: "grid",
      width: "100%",
      maxWidth: 860,
      gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "repeat(2, minmax(0, 1fr))" },
      columnGap: 2.5,
      rowGap: 1.5,
    }}
  >
    {children}
  </Box>
);

const Section = ({ icon, title, headerContent, count, children, defaultExpanded = false }) => (
  <Box
    component="section"
    sx={{
      borderRadius: 1,
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
      overflow: "hidden",
      "& .qub-simple-accordion > div:first-of-type": {
        bgcolor: "action.hover",
        minHeight: 54,
      },
    }}
  >
    <CustomSimpleAccordion
      defaultExpanded={defaultExpanded}
      title={<Stack direction="row" spacing={1.25} alignItems="center" useFlexGap flexWrap="wrap">
        <Box sx={{ color: "primary.main", display: "flex" }}>{icon}</Box>
        <Typography variant="h6" fontWeight={700}>
          {title}
        </Typography>
        {headerContent}
        {count !== undefined && <Chip size="small" label={count} />}
      </Stack>}
      content={children}
    />
  </Box>
);

const ParentDetails = ({ title, parent }) => (
  <Box>
    <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
      {title}
    </Typography>
    <FieldGrid>
      <Field label="ΑΜΚΑ" value={parent?.amka} />
      <Field label="ΑΦΜ" value={parent?.afm} />
      <Field label="Όνομα" value={parent?.firstName} />
      <Field label="Επώνυμο" value={parent?.lastName} />
      <Field label="Διεύθυνση" value={parent?.address} />
      <Field label="Τηλέφωνο" value={parent?.phone} />
      <Field label="Ιθαγένεια" value={parent?.citizenship} />
      <Field label="Επάγγελμα" value={parent?.jobTitle} />
      <Field label="Σωματική υγεία" value={parent?.physicalHealth} />
      <Field label="Ψυχική υγεία" value={parent?.psychologicalHealth} />
      <Field label="Νοητική κατάσταση" value={parent?.mentalState} />
    </FieldGrid>
    {parent?.comments && (
      <Box sx={{ mt: 2 }}>
        <Field
          label={`Σχόλιο για ${title === "Μητέρα" ? "τη μητέρα" : "τον πατέρα"}`}
          value={parent.comments}
        />
      </Box>
    )}
  </Box>
);

const SurveyDetails = ({ survey, onPreview, openingFileId }) => (
  <>
    {survey.comments && <Alert severity="info" sx={{ mb: 2 }}>{survey.comments}</Alert>}

    {(survey.sections || []).length ? (
      <CompactGrid
        items={survey.sections.map((section) => ({
          ...section,
          answersText: (section.answers || []).map((answer) => answer.answer).join(", ") || "-",
        }))}
        rowHeight={(item) => Math.max(34, 10 + 18 * Math.ceil(Math.max(item.section?.length / 45 || 1, item.answersText.length / 70)))}
      >
        <FlexGridColumn binding="section" header="Ενότητα" width="2*" minWidth={220}>
          <FlexGridCellTemplate cellType="Cell" template={({ item }) => <Box sx={{ whiteSpace: "normal", fontWeight: 600 }}>{item.section}</Box>} />
        </FlexGridColumn>
        <FlexGridColumn binding="answersText" header="Επιλεγμένες απαντήσεις" width="3*" minWidth={280}>
          <FlexGridCellTemplate cellType="Cell" template={({ item }) => <Box sx={{ whiteSpace: "normal" }}>{item.answersText}</Box>} />
        </FlexGridColumn>
      </CompactGrid>
    ) : <Alert severity="info">Δεν υπάρχουν καταχωρισμένες απαντήσεις.</Alert>}

    <Typography fontWeight={700} sx={{ mt: 2, mb: 1 }}>
      Συνδεδεμένα αρχεία
    </Typography>
    {(survey.attachments || []).length ? (
      <CompactGrid items={survey.attachments}>
        <FlexGridColumn binding="name" header="Αρχείο" width="3*" minWidth={220} />
        <FlexGridColumn binding="createdAt" header="Ημερομηνία" width={145}>
          <FlexGridCellTemplate cellType="Cell" template={({ item }) => formatDate(item.createdAt)} />
        </FlexGridColumn>
        <FlexGridColumn header="Ενέργεια" width={145} minWidth={145}>
          <FlexGridCellTemplate cellType="Cell" template={({ item }) => (
            <Button size="small" startIcon={<VisibilityOutlined />} disabled={openingFileId === item.id} onClick={() => onPreview(item)}>Προβολή</Button>
          )} />
        </FlexGridColumn>
      </CompactGrid>
    ) : (
      <Typography variant="body2" color="text.secondary">
        Δεν υπάρχουν συνδεδεμένα αρχεία.
      </Typography>
    )}
  </>
);

const SurveyDialog = ({ survey, onClose, onPreview, openingFileId, fullScreen }) => (
  <Dialog
    open
    onClose={onClose}
    fullWidth
    fullScreen={fullScreen}
    maxWidth="lg"
    aria-labelledby="social-survey-dialog-title"
  >
    <DialogTitle id="social-survey-dialog-title" sx={{ pr: 6, pb: 1 }}>
      <Typography component="span" variant="h6" fontWeight={700}>
        {survey.type || "Κοινωνική έρευνα"}
      </Typography>
      <IconButton
        aria-label="Κλείσιμο προβολής κοινωνικής έρευνας"
        onClick={onClose}
        sx={{ position: "absolute", right: 12, top: 12 }}
      >
        <CloseOutlined />
      </IconButton>
    </DialogTitle>
    <DialogContent dividers sx={{ px: { xs: 2, sm: 3 } }}>
      <Stack direction="row" alignItems="center" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {`Δημιουργήθηκε ${formatDate(survey.createdAt)}`}
          {survey.isSubmitted && survey.submittedAt
            ? ` · Υποβλήθηκε ${formatDate(survey.submittedAt)}`
            : ""}
        </Typography>
        <Chip
          size="small"
          color={survey.isSubmitted ? "success" : "warning"}
          label={survey.isSubmitted ? "Έχει υποβληθεί" : "Σε αποθήκευση"}
        />
      </Stack>
      <SurveyDetails survey={survey} onPreview={onPreview} openingFileId={openingFileId} />
    </DialogContent>
    <DialogActions sx={{ px: { xs: 2, sm: 3 } }}>
      <Button onClick={onClose}>Κλείσιμο</Button>
    </DialogActions>
  </Dialog>
);

const ChildProfile = () => {
  const { applicationId, childId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestedApartmentId = searchParams.get("apartmentId");
  const cameFromApartment = searchParams.get("from") === "apartment";
  const theme = useTheme();
  const fullScreenSurvey = useMediaQuery(theme.breakpoints.down("sm"));
  const [child, setChild] = useState(null);
  const [selectedSurveyId, setSelectedSurveyId] = useState(null);
  const [historySort, setHistorySort] = useState({ key: null, direction: "ascending" });
  const [photoUrl, setPhotoUrl] = useState("");
  const [fileToPreview, setFileToPreview] = useState(null);
  const [openingFileId, setOpeningFileId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    getMhdChild(applicationId, childId)
      .then((response) => {
        if (!active) return;
        if (response?.succeeded === false || !response?.data) {
          setError(response?.message || "Δεν βρέθηκε ο ωφελούμενος.");
          return;
        }
        setChild(response.data);
      })
      .catch((requestError) => {
        if (active) {
          setError(requestError?.message || "Δεν φορτώθηκε η καρτέλα του ωφελούμενου.");
        }
      })
      .finally(() => active && setLoading(false));

    return () => {
      active = false;
    };
  }, [applicationId, childId]);

  const navigationContext = location.state?.mhdBreadcrumb;
  const hasMatchingContext = navigationContext?.applicationId === applicationId
    && navigationContext?.childId === childId
    && navigationContext?.apartment?.id?.toLowerCase() === requestedApartmentId?.toLowerCase();
  const apartment = hasMatchingContext
    ? navigationContext.apartment
    : requestedApartmentId ? { id: requestedApartmentId, name: "Διαμέρισμα" } : null;

  const apartmentPath = apartment && `/govnr/mhds/${applicationId}/apartments/${apartment.id}`;
  const backPath = cameFromApartment && apartmentPath ? apartmentPath : `/govnr/mhds/${applicationId}`;

  useEffect(() => {
    let active = true;
    let objectUrl = "";

    if (!child?.photo?.id) {
      setPhotoUrl("");
      return undefined;
    }

    getMhdChildAttachmentBlob({
      applicationId,
      childId,
      attachmentId: child.photo.id,
      source: child.photo.source,
    })
      .then((blob) => {
        if (!active) return;
        objectUrl = window.URL.createObjectURL(blob);
        setPhotoUrl(objectUrl);
      })
      .catch(() => active && setPhotoUrl(""));

    return () => {
      active = false;
      if (objectUrl) window.URL.revokeObjectURL(objectUrl);
    };
  }, [applicationId, childId, child?.photo?.id, child?.photo?.source]);

  const selectedSurvey = useMemo(
    () => (child?.socialSurveys || []).find((survey) => survey.surveyId === selectedSurveyId),
    [child?.socialSurveys, selectedSurveyId],
  );

  const sortedHistory = useMemo(() => {
    const history = child?.history || [];
    if (!historySort.key) return history;

    const dateColumn = historySort.key === "date" || historySort.key === "createdAt";
    const direction = historySort.direction === "ascending" ? 1 : -1;
    return [...history].sort((left, right) => {
      if (dateColumn) {
        const leftDate = Date.parse(left[historySort.key] || "") || 0;
        const rightDate = Date.parse(right[historySort.key] || "") || 0;
        return (leftDate - rightDate) * direction;
      }

      return String(left[historySort.key] || "").localeCompare(
        String(right[historySort.key] || ""),
        "el",
        { sensitivity: "base" },
      ) * direction;
    });
  }, [child?.history, historySort]);

  const sortHistory = (key) => {
    setHistorySort((current) => ({
      key,
      direction: current.key === key && current.direction === "ascending" ? "descending" : "ascending",
    }));
  };

  const handlePreview = async (document) => {
    if (!document?.id) return;
    setOpeningFileId(document.id);
    try {
      const blob = await getMhdChildAttachmentBlob({
        applicationId,
        childId,
        attachmentId: document.id,
        source: document.source,
      });
      setFileToPreview({ blob, name: document.name || "Αρχείο" });
    } catch (previewError) {
      enqueueSnackbar(previewError?.message || "Δεν ήταν δυνατή η προεπισκόπηση του αρχείου.", {
        variant: "error",
      });
    } finally {
      setOpeningFileId(null);
    }
  };

  if (loading) {
    return <Box sx={{ minHeight: 320, display: "grid", placeItems: "center" }}><CircularProgress /></Box>;
  }

  if (error || !child) {
    return <Alert severity="error" sx={{ m: 3 }}>{error || "Δεν βρέθηκε ο ωφελούμενος."}</Alert>;
  }

  return (
    <Box
      sx={{
        maxWidth: 1280,
        mx: "auto",
        px: { xs: 0, sm: 2, md: 3, lg: 4 },
        py: { xs: 1.5, md: 2 },
      }}
    >
      {fileToPreview && (
        <MhdFilePreviewDialog file={fileToPreview} onClose={() => setFileToPreview(null)} />
      )}
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 1.5 }}>
        <Box sx={{ minWidth: 0, flex: "1 1 320px", "& .MuiBreadcrumbs-root": { mb: 0 } }}>
          <ProfileBreadcrumbs
            profile={{ applicationId, name: hasMatchingContext ? navigationContext.mhdName : undefined }}
            apartment={apartment}
            childName={`Καρτέλα ωφελούμενου: ${child.lastName} ${child.firstName}`}
          />
        </Box>
        <Button
          variant="outlined"
          startIcon={<ArrowBackOutlined />}
          onClick={() => navigate(backPath)}
          sx={{ flexShrink: 0 }}
        >
          Επιστροφή
        </Button>
      </Stack>
      <Stack spacing={2}>
        <Section
          title="Στοιχεία Μονάδας"
          icon={<BusinessOutlined />}
          headerContent={child.currentInstitute && (
            <Typography variant="body2" color="text.secondary">{child.currentInstitute}</Typography>
          )}
          defaultExpanded
        >
          <FieldGrid>
            <Field label="Δομή" value={child.mainInstitute} />
            <Field label="Κοινωνικός λειτουργός" value={child.socialWorker} />
            <Field label="Ημ/νία εισαγωγής" value={formatDate(child.dateOfAppearance)} />
            <Field label="Τρόπος εισαγωγής" value={child.enrollmentType} />
            <Field label="Λόγος εισαγωγής" value={child.enrollmentReason} />
          </FieldGrid>
        </Section>

        <Section
          title="Στοιχεία Ανηλίκου"
          icon={<ChildCareOutlined />}
          headerContent={(
            <>
              <Typography component="h1" variant="body2" fontWeight={600}>
                {child.lastName} {child.firstName}
              </Typography>
              <Chip size="small" color="primary" label={child.status || "Χωρίς κατάσταση"} />
            </>
          )}
          defaultExpanded
        >
          <Stack direction={{ xs: "column", md: "row" }} spacing={3} alignItems="flex-start" sx={{ maxWidth: 1080 }}>
            <Box sx={{ flex: 1, width: "100%" }}>
              <FieldGrid>
                <Field label="Όνομα" value={child.firstName} />
                <Field label="Επώνυμο" value={child.lastName} />
                <Field label="ΑΜΚΑ" value={child.amka} />
                <Field label="ΑΦΜ" value={child.afm} />
                <Field label="Αρ. μητρώου" value={child.recordNumber} />
                <Field label="ΑΜ μονάδας" value={child.identifierId} />
                <Field label="Ημ/νία γέννησης" value={formatDate(child.dateOfBirth)} />
                <Field label="Πιστοποιημένη ημ/νία γέννησης" value={child.dateOfBirthVerified ? "Ναι" : "Όχι"} />
                <Field label="Τόπος γέννησης" value={child.placeOfBirth} />
                <Field label="Φύλο" value={child.gender} />
                <Field label="Εθνικότητα" value={child.nationality} />
                <Field label="Ιθαγένεια" value={child.citizenship} />
                <Field label="Αριθμός αδελφών" value={child.numOfSiblings?.toString()} />
                <Field label="Περιφέρεια" value={child.region} />
                <Field label="Περιφερειακή ενότητα" value={child.prefecture} />
                <Field label="Δήμος" value={child.municipality} />
              </FieldGrid>
            </Box>
            <Paper
              variant="outlined"
              sx={{
                width: 170,
                minWidth: 170,
                height: 190,
                display: "grid",
                placeItems: "center",
                overflow: "hidden",
                bgcolor: "action.hover",
              }}
            >
              {photoUrl ? (
                <Box component="img" src={photoUrl} alt="Φωτογραφία ανηλίκου" sx={{ width: "100%", height: "100%", objectFit: "contain" }} />
              ) : (
                <Typography variant="caption" color="text.secondary">Δεν υπάρχει φωτογραφία</Typography>
              )}
            </Paper>
          </Stack>
          {child.comments && <Box sx={{ mt: 2 }}><Field label="Παρατηρήσεις" value={child.comments} /></Box>}
        </Section>

        <Section title="Γονείς" icon={<FamilyRestroomOutlined />}>
          <ParentDetails title="Μητέρα" parent={child.mother} />
          <Divider sx={{ my: 3 }} />
          <ParentDetails title="Πατέρας" parent={child.father} />
        </Section>

        <Section title="Συγγενείς" icon={<GroupsOutlined />} count={child.relatives?.length || 0}>
          {(child.relatives || []).length ? (
            <CompactGrid items={child.relatives}>
              <FlexGridColumn binding="name" header="Ονοματεπώνυμο" width="2*" minWidth={200} />
              <FlexGridColumn binding="amka" header="ΑΜΚΑ" width={145} />
              <FlexGridColumn binding="afm" header="ΑΦΜ" width={125} />
              <FlexGridColumn binding="relationship" header="Οικ. σχέση με το ανήλικο" width="2*" minWidth={180} />
            </CompactGrid>
          ) : <Alert severity="info">Δεν υπάρχουν καταχωρισμένοι συγγενείς.</Alert>}
          {child.relativeComments && <Box sx={{ mt: 2 }}><Field label="Σχόλιο για τους συγγενείς" value={child.relativeComments} /></Box>}
        </Section>

        <Section
          title="Έγγραφα"
          icon={<AttachFileOutlined />}
          count={child.documents?.length || 0}
        >
          {(child.documents || []).length ? (
            <CompactGrid items={child.documents}>
              <FlexGridColumn binding="category" header="Κατηγορία" width="2*" minWidth={225}>
                <FlexGridCellTemplate cellType="Cell" template={({ item }) => item.category || "-"} />
              </FlexGridColumn>
              <FlexGridColumn binding="name" header="Αρχείο" width="3*" minWidth={260}>
                <FlexGridCellTemplate cellType="Cell" template={({ item }) => (
                  <Button
                    size="small"
                    startIcon={<VisibilityOutlined />}
                    disabled={openingFileId === item.id}
                    onClick={() => handlePreview(item)}
                    sx={{ textTransform: "none", justifyContent: "flex-start" }}
                  >
                    {item.name}
                  </Button>
                )} />
              </FlexGridColumn>
              <FlexGridColumn binding="createdAt" header="Ημ/νία δημιουργίας" width={180}>
                <FlexGridCellTemplate cellType="Cell" template={({ item }) => formatDateTime(item.createdAt)} />
              </FlexGridColumn>
            </CompactGrid>
          ) : <Alert severity="info">Δεν υπάρχουν έγγραφα.</Alert>}
        </Section>

        <Section title="Στοιχεία κοινωνικού ιστορικού" icon={<AssignmentOutlined />} count={child.socialSurveys?.length || 0}>
          {(child.socialSurveys || []).length ? (
            <CompactGrid items={child.socialSurveys.map((survey) => ({
              ...survey,
              socialWorker: survey.socialWorker || "-",
              statusText: survey.isSubmitted ? "Έχει υποβληθεί" : "Σε αποθήκευση",
            }))}>
              <FlexGridColumn binding="createdAt" header="Ημερομηνία" width={145}>
                <FlexGridCellTemplate cellType="Cell" template={({ item }) => formatDate(item.createdAt)} />
              </FlexGridColumn>
              <FlexGridColumn binding="socialWorker" header="Κοινωνικός λειτουργός" width="2*" minWidth={180} />
              <FlexGridColumn binding="statusText" header="Κατάσταση" width={145} />
              <FlexGridColumn binding="type" header="Τύπος" width="2*" minWidth={170} />
              <FlexGridColumn header="Ενέργεια" width={95}>
                <FlexGridCellTemplate cellType="Cell" template={({ item }) => (
                  <Button
                    size="small"
                    aria-haspopup="dialog"
                    aria-label={`Προβολή ${item.type || "κοινωνικής έρευνας"} ${formatDate(item.createdAt)}`}
                    onClick={() => setSelectedSurveyId(item.surveyId)}
                  >
                    Προβολή
                  </Button>
                )} />
              </FlexGridColumn>
            </CompactGrid>
          ) : <Alert severity="info">Δεν υπάρχουν ΑΣΟΑ ή ΑΣΔ.</Alert>}
        </Section>

        {selectedSurvey && (
          <SurveyDialog
            survey={selectedSurvey}
            onClose={() => setSelectedSurveyId(null)}
            onPreview={handlePreview}
            openingFileId={openingFileId}
            fullScreen={fullScreenSurvey}
          />
        )}

        <Paper variant="outlined" sx={{ overflow: "hidden" }}>
          <Stack
            direction="row"
            spacing={1.25}
            alignItems="center"
            sx={{ px: 2, py: 1.75, bgcolor: "var(--color-primary)", color: "#fff" }}
          >
            <HistoryOutlined sx={{ color: "inherit" }} />
            <Typography variant="h6" fontWeight={700} sx={{ color: "#fff" }}>Ιστορικό</Typography>
            <Chip
              size="small"
              label={child.history?.length || 0}
              sx={{ bgcolor: "rgba(255, 255, 255, 0.18)", color: "inherit", fontWeight: 600 }}
            />
          </Stack>
          {sortedHistory.length ? (
            <div className="mhd-profile-history-scroll">
              <table className="mhd-profile-history-table">
                <colgroup>
                  <col style={{ width: "16%" }} />
                  <col style={{ width: "17%" }} />
                  <col style={{ width: "29%" }} />
                  <col style={{ width: "18%" }} />
                  <col style={{ width: "20%" }} />
                </colgroup>
                <thead>
                  <tr>
                    <HistorySortHeader label="Ημερομηνία συμβάντος" column="date" sortConfig={historySort} onSort={sortHistory} />
                    <HistorySortHeader label="Ημερομηνία δημιουργίας" column="createdAt" sortConfig={historySort} onSort={sortHistory} />
                    <HistorySortHeader label="Μήνυμα" column="message" sortConfig={historySort} onSort={sortHistory} />
                    <th scope="col">Αρχεία</th>
                    <HistorySortHeader label="Όνομα" column="createdBy" sortConfig={historySort} onSort={sortHistory} />
                  </tr>
                </thead>
                <tbody>
                  {sortedHistory.map((item) => (
                    <tr key={item.id}>
                      <td>{formatDateTime(item.date)}</td>
                      <td>{formatDateTime(item.createdAt)}</td>
                      <td className="mhd-profile-history-message">{item.message || "-"}</td>
                      <td><FileActions files={item.attachments} onPreview={handlePreview} openingFileId={openingFileId} /></td>
                      <td>{item.createdBy || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <Box sx={{ p: { xs: 2, md: 3 } }}><Alert severity="info">Δεν υπάρχει ιστορικό.</Alert></Box>}
        </Paper>
      </Stack>
    </Box>
  );
};

export default ChildProfile;
